/*
 * Copyright (c) Worldline 2023.
 */

import {NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';

import {AppComponent} from './app.component';
import {AppRoutingModule} from './app-routing.module';
import {UpgradeModule} from '@angular/upgrade/static';

import {PdfViewerComponent} from "./components/pdf-viewer/pdf-viewer.component";
import {TranslateCompiler, TranslateLoader, TranslateModule} from "@ngx-translate/core";
import {HttpClient, HttpClientModule} from "@angular/common/http";
import {TranslateHttpLoader} from "@ngx-translate/http-loader";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {DatePipe} from "@angular/common";
import {CertificatePopupComponent} from './components/certificate-popup/certificate-popup.component';
import {NgxSpinnerModule} from "ngx-spinner";
import {SignaturesComponent} from './components/signatures/signatures.component';
import {StructureComponent} from "./components/structure/structure.component";
import {AgreementsComponent} from './components/agreements/agreements.component';
import {SignatureActionPipe} from './pipe/signature-action.pipe';
import {WlCertificateApprovalComponent} from './components/wl-certificate-approval/wl-certificate-approval.component';
import {SafeHtmlPipe} from "./pipe/safe-html.pipe";
import {TranslateMessageFormatCompiler} from "ngx-translate-messageformat-compiler";
import {WlHandSignComponent} from './components/wl-hand-sign/wl-hand-sign.component';
import {SmartphoneAgreementComponent} from './components/smartphone-agreement/smartphone-agreement.component';
import {EvidencesComponent} from './components/evidences/evidences.component';
import {WlElectronicSignaturePurposeComponent} from './components/wl-electronic-signature-purpose/wl-electronic-signature-purpose.component';
import {
    WlElectronicSignaturePurposePopupComponent
} from './components/wl-electronic-signature-purpose-popup/wl-electronic-signature-purpose-popup.component';
import {ImagePreviewPopupComponent} from "./components/image-preview-popup/image-preview-popup.component";
import {WlEvidenceComponent} from "./components/wl-evidence/wl-evidence.component";
import {WlPopoverComponent} from "./components/wl-popover/wl-popover.component";
import {WlEvidencePartComponent} from "./components/wl-evidence-part/wl-evidence-part.component";
import {WlIbanFormComponent} from "./components/wl-iban-form/wl-iban-form.component";
import {WlEvidenceToCropComponent} from "./components/wl-evidence-to-crop/wl-evidence-to-crop.component";
import {ConfirmCancellationComponent} from './components/confirm-cancellation/confirm-cancellation.component';
import {CancellationComponent} from './components/cancellation/cancellation.component';
import {UnexpectedErrorComponent} from './components/unexpected-error/unexpected-error.component';
import {Page404Component} from './components/page404/page404.component';
import {OnHoldComponent} from './components/on-hold/on-hold.component';
import {ErrorComponent} from './components/error/error.component';
import {TokenRenewalComponent} from './components/token-renewal/token-renewal.component';
import {HelpComponent} from './components/help/help.component';
import {WlFooterComponent} from './components/wl-footer/wl-footer.component';
import {WlCookieAgreementComponent} from './components/wl-cookie-agreement/wl-cookie-agreement.component';
import {ContactComponent} from './components/contact/contact.component';
import {LegalNoticesComponent} from './components/legal-notices/legal-notices.component';
import {PendingSignatureComponent} from "./components/pending-signature/pending-signature.component";
import {SessionComponent} from "./components/session/session.component";
import {CookieService} from 'ngx-cookie-service';
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {ResumeComponent} from './components/resume/resume.component';
import {IdlePopupComponent} from './components/idle-popup/idle-popup.component';
import {NgIdleModule} from "@ng-idle/core";
import {TimeoutPopupComponent} from './components/timeout-popup/timeout-popup.component';
import {RedirectComponent} from './components/redirect/redirect.component';
import {NgbAccordionModule} from "@ng-bootstrap/ng-bootstrap";
import {CustomizationComponent} from "./components/customization/customization.component";
import {ColorPickerModule} from "ngx-color-picker";
import {EmbeddedLinkComponent} from "./components/wl-certificate-approval/embedded-link.component";

export function translateHttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, '/config/front/locales?lang=', '');
}

@NgModule({
    declarations: [
        AppComponent,
        PdfViewerComponent,
        CertificatePopupComponent,
        SignaturesComponent,
        StructureComponent,
        AgreementsComponent,
        SignatureActionPipe,
        SafeHtmlPipe,
        WlCertificateApprovalComponent,
        WlHandSignComponent,
        SmartphoneAgreementComponent,
        EvidencesComponent,
        WlElectronicSignaturePurposeComponent,
        WlElectronicSignaturePurposePopupComponent,
        ImagePreviewPopupComponent,
        WlEvidenceComponent,
        WlPopoverComponent,
        WlEvidencePartComponent,
        WlIbanFormComponent,
        WlEvidenceToCropComponent,
        ConfirmCancellationComponent,
        CancellationComponent,
        UnexpectedErrorComponent,
        Page404Component,
        OnHoldComponent,
        EmbeddedLinkComponent,
        TokenRenewalComponent,
        HelpComponent,
        WlFooterComponent,
        WlCookieAgreementComponent,
        ContactComponent,
        CustomizationComponent,
        LegalNoticesComponent,
        PendingSignatureComponent,
        ErrorComponent,
        SessionComponent,
        ResumeComponent,
        IdlePopupComponent,
        TimeoutPopupComponent,
        RedirectComponent
    ],
    imports: [
        FormsModule,
        BrowserModule,
        ReactiveFormsModule,
        ColorPickerModule,
        AppRoutingModule,
        UpgradeModule,
        HttpClientModule,
        TranslateModule.forRoot({
            defaultLanguage: 'fr',
            loader: {
                provide: TranslateLoader,
                useFactory: (translateHttpLoaderFactory),
                deps: [HttpClient]
            },
            compiler: {
                provide: TranslateCompiler,
                useClass: TranslateMessageFormatCompiler
            }
        }),
        BrowserAnimationsModule,
        NgxSpinnerModule,
        NgIdleModule.forRoot(),
        NgbAccordionModule

    ],
    providers: [
        DatePipe,
        CookieService
    ],
    bootstrap: [AppComponent]
})
export class AppModule {

    constructor() {
    }
}
